// components/webhook/EditWebhookModal.jsx
import { useEffect } from "react";
import PropTypes from "prop-types";
import GModal from "../../design/components/GModal";
import "./Developer.css";
import {
  CustomSwitch,
  CustomSelect,
} from "../../design/components/customComponents";
import { Box, Typography, TextField, IconButton } from "@mui/material";
import Button from "../../design/Button";
import { CustomInput } from "../../design/components/customComponents";
import CloseIcon from "@mui/icons-material/Close";
import { plusIcon } from "../../../utility-functions/assetLoader";
import POCEmailContainer from "./POCEmailContainer";
import colors from "../../design/colors";
import { validateEmail } from "../../../utility-functions";

const isHeaderInvalid = (h) => !h.key.trim() || !h.value.trim();
const EditWebhookModal = ({
  visible,
  closeModal,
  webhook,
  setWebhook,
  onSave,
  editSave,
  setEditSave,
  isRedirectUrlValid,
}) => {

  const handleHeaderChange = (index, field, value) => {
    setWebhook((prev) => {
      const updatedHeaders = [...(prev.headers || [])];
      updatedHeaders[index] = {
        ...updatedHeaders[index],
        [field]: value,
      };
      return {
        ...prev,
        headers: updatedHeaders,
      };
    });
  };

  const handleAddHeader = () => {
    if (webhook.headers.length < 5)
      setWebhook({
        ...webhook,
        headers: [...(webhook.headers || []), { key: "", value: "" }],
      });
  };

  const handleRemoveHeader = (index) => {
    const updated = [...(webhook.headers || [])];
    updated.splice(index, 1);
    setWebhook({ ...webhook, headers: updated });
  };

  const handleEmailChange = (e, index) => {
    setWebhook((prev) => {
      const emails = [...(prev.poc_emails || [])];
      emails[index] = e.target.value;
      return {
        ...prev,
        poc_emails: emails,
      };
    });
  };

  const handleAddEmail = () => {
    if ((webhook.poc_emails || []).length < 5) {
      setWebhook((prev) => ({
        ...prev,
        poc_emails: [...(prev.poc_emails || []), ""],
      }));
    }
  };

  const handleRemoveEmail = (index) => {
    setWebhook((prev) => {
      const emails = [...(prev.poc_emails || [])];
      emails.splice(index, 1);
      return {
        ...prev,
        poc_emails: emails,
      };
    });
  };

  //Effects
  useEffect(() => {
    const allEmailsValid = webhook?.poc_emails?.every((email) =>
      validateEmail(email)
    );
    const isUrlValid =
      webhook?.callback_url?.trim() && isRedirectUrlValid(webhook.callback_url);
    const areHeadersValid = (webhook.headers || []).every(
      (h) => h.key.trim() && h.value.trim()
    );
    setEditSave(isUrlValid && areHeadersValid && allEmailsValid);
  }, [webhook]);

  return (
    <GModal
      visible={visible}
      heading="Edit webhook"
      closeModal={closeModal}
      modalStyle={{
        width: "448px",
        padding: "24px 32px",
        gap: "20px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        maxHeight: "350px",
      }}
      body={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            maxWidth: 400,
          }}
        >
          {/* Event Type */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Event type
            </Typography>
            <CustomSelect
              value={webhook?.event || ""}
              options={[
                { value: webhook?.event, displayValue: webhook?.event },
              ]}
              disabled={true}
              selectStyles={{
                width: "100%",
                height: "40px",
                borderRadius: "6px",
                backgroundColor: "#F9FAFB",
                fontSize: "14px",
              }}
              renderVal={(val) => (
                <p className="t7 regular-font render-value-no-overflow">
                  {val}
                </p>
              )}
            />
          </Box>

          {/* Callback URL */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              URL
            </Typography>
            <CustomInput
              sx={{ minWidth: 400 }}
              handleFocus={() => {}}
              name="callbackUrl"
              type="url"
              placeholder="https://example.com/"
              labelStyles={{ marginBottom: "0rem" }}
              value={{
                value: webhook?.callback_url || "",
                error:
                  webhook?.callback_url &&
                  !isRedirectUrlValid(webhook?.callback_url)
                    ? "Invalid URL format"
                    : "",
              }}
              handleChange={(e) =>
                setWebhook({ ...webhook, callback_url: e.target.value })
              }
            />
          </Box>

          <POCEmailContainer
            pocEmails={webhook?.poc_emails || []}
            handleAddEmail={handleAddEmail}
            handleRemoveEmail={handleRemoveEmail}
            handleEmailChange={handleEmailChange}
          />

          {/* Custom Headers */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.3 }}>
              Custom headers{" "}
              <Typography
                variant="caption"
                component="span"
                color="text.secondary"
              >
                (optional)
              </Typography>
            </Typography>

            {(webhook.headers || []).map((header, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                sx={{ mb: 1, gap: "6px" }}
              >
                <TextField
                  size="small"
                  placeholder="Key"
                  error={isHeaderInvalid(header)}
                  value={header.key}
                  onChange={(e) =>
                    handleHeaderChange(index, "key", e.target.value)
                  }
                  sx={{
                    width: "176px",
                    marginTop: "4px",
                    marginBottom: "4px",
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: colors.cgreen_600,
                      },
                    },
                  }}
                />
                <TextField
                  size="small"
                  error={isHeaderInvalid(header)}
                  placeholder="Value"
                  value={header.value}
                  onChange={(e) =>
                    handleHeaderChange(index, "value", e.target.value)
                  }
                  sx={{
                    width: "176px",
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: colors.cgreen_600,
                      },
                    },
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveHeader(index)}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              buttonText="Add header"
              onClick={handleAddHeader}
              variant="outlined"
              hierarchy="white"
              disabled={webhook.headers?.length >= 5}
              icon={{
                startIcon: plusIcon(
                  webhook.headers?.length >= 5 ? colors.cgrey_300 : colors.cgreen_600
                ),
              }}
              tooltipMessage={
                webhook.headers?.length >= 5
                  ? "Max 5 custom headers can be added "
                  : ""
              }
              styleOverride={{
                color: colors.cgreen_600,
                width: "132px",
                padding: "8px 12px",
                fontWeight: "500",
                height: "36px",
              }}
            />
          </Box>

          {/* Status */}
          <Box display="flex" alignItems="center" gap={2}>
            <Box display="flex" flexDirection="column">
              <Typography variant="subtitle2">Status</Typography>
              <CustomSwitch
                switchMT={8}
                checked={!!webhook?.active}
                onChange={(e) =>
                  setWebhook({ ...webhook, active: e.target.checked })
                }
                label={webhook?.active ? "Active" : "Inactive"}
              />
            </Box>
          </Box>
        </Box>
      }
      bottomButtons={[
        <Box
          key="edit-actions"
          sx={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Button
            buttonText="Cancel"
            onClick={closeModal}
            variant="outlined"
            hierarchy="white"
            styleOverride={{ width: "75px", height: "36px", fontWeight: "500" }}
          />
          <Button
            buttonText="Save"
            disabled={!editSave}
            onClick={onSave}
            variant="contained"
            hierarchy="green"
            styleOverride={{ width: "73px", height: "36px", fontWeight: "500" }}
          />
        </Box>,
      ]}
    />
  );
};

EditWebhookModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  webhook: PropTypes.object.isRequired,
  setWebhook: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditWebhookModal;
